import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, StatCard, LoadingState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, MagnifyingGlass, CheckCircle, ClockClockwise, Buildings, HouseLine, X,
    Toolbox, Storefront, User, ArrowSquareOut, LockKey, WarningCircle, CurrencyInr, Receipt,
    DownloadSimple, Printer,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link, useLocation, useNavigate } from "react-router-dom";

const TYPES = ["COMPLAINT", "PREVENTIVE", "ROUTINE", "REPAIR", "INSPECTION", "PROJECT", "OTHER"];
const STATUSES = ["DRAFT", "OPEN", "ASSIGNED", "IN_PROGRESS", "ON_HOLD", "COMPLETED", "CANCELLED", "CLOSED"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const COMMON_AREAS = ["Lift / Elevator", "Entrance / Lobby", "Corridor", "Staircase", "Garden", "Parking Area", "Clubhouse", "Gym", "Swimming Pool", "Security Gate", "Children's Play Area", "Terrace", "Water Tank / Pump Room", "Society Office", "Other Common Area"];

const label = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const fmt = (dt) => { if (!dt) return "—"; try { return new Date(dt).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return dt; } };
const fmtDate = (dt) => { if (!dt) return "—"; try { return new Date(dt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return dt; } };

const STATUS_VARIANT = { DRAFT: "neutral", OPEN: "warn", ASSIGNED: "default", IN_PROGRESS: "default", ON_HOLD: "warn", COMPLETED: "success", CANCELLED: "neutral", CLOSED: "neutral" };
const DUE_VARIANT = { overdue: "danger", due_today: "warn", due_soon: "warn", scheduled: "neutral", resolved: "success" };
const PRIORITY_VARIANT = { low: "neutral", medium: "default", high: "warn", urgent: "danger" };
const FR_LABEL = { SOCIETY: "Society Expense", FLAT: "Flat Invoice" };

function dueLabel(wo) {
    if (!wo.due_date) return "—";
    const s = wo.due_state;
    if (s === "overdue") return `Overdue ${wo.overdue_days}d`;
    if (s === "due_today") return "Due today";
    if (s === "due_soon") return "Due soon";
    return fmtDate(wo.due_date);
}

// ---------- Detail dialog ----------
function WODetail({ id, providers, onClose, onChange }) {
    const [wo, setWO] = useState(null);
    const [timeline, setTimeline] = useState([]);
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [completeOpen, setCompleteOpen] = useState(false);
    const [completion, setCompletion] = useState({ summary: "", cost: "" });
    const [commentBody, setCommentBody] = useState("");
    const [commentKind, setCommentKind] = useState("public");
    const [approvedCostInput, setApprovedCostInput] = useState("");
    const [postOpen, setPostOpen] = useState(false);
    const [posting, setPosting] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [w, t, c] = await Promise.all([
                api.get(`/work-orders/${id}`),
                api.get(`/work-orders/${id}/timeline`),
                api.get(`/work-orders/${id}/comments`),
            ]);
            setWO(w.data); setTimeline(t.data); setComments(c.data);
            setApprovedCostInput(w.data.approved_cost != null ? String(w.data.approved_cost) : "");
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function changeStatus(next) {
        try { await api.post(`/work-orders/${id}/status`, { status: next }); toast.success(`Status → ${label(next)}`); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function assign(pid) {
        try { await api.post(`/work-orders/${id}/assign`, { assignee_id: pid }); toast.success("Assigned"); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function submitComplete() {
        if (!completion.summary.trim()) { toast.error("Completion summary is required"); return; }
        try {
            const payload = { completion_summary: completion.summary };
            if (completion.cost) payload.actual_cost = parseFloat(completion.cost);
            await api.post(`/work-orders/${id}/complete`, payload);
            toast.success("Marked completed"); setCompleteOpen(false); setCompletion({ summary: "", cost: "" });
            await load(); onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function closeWO() {
        try { await api.post(`/work-orders/${id}/close`); toast.success("Closed"); await load(); onChange?.(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function postComment() {
        if (!commentBody.trim()) return;
        try {
            await api.post(`/work-orders/${id}/comments`, { body: commentBody, kind: commentKind });
            setCommentBody(""); toast.success(commentKind === "internal" ? "Internal note added" : "Comment posted"); await load();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function saveApprovedCost() {
        try {
            await api.patch(`/work-orders/${id}`, { approved_cost: parseFloat(approvedCostInput) });
            toast.success("Approved cost updated"); await load(); onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function setResponsibility(v) {
        try {
            await api.patch(`/work-orders/${id}`, { financial_responsibility: v });
            toast.success("Financial responsibility updated"); await load(); onChange?.();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function postFinancial() {
        setPosting(true);
        try {
            const r = await api.post(`/work-orders/${id}/post-financial`, {});
            toast.success(r.data.financial_transaction_type === "INVOICE" ? "Invoice created for the flat" : "Society expense recorded");
            setPostOpen(false); await load(); onChange?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setPosting(false); }
    }
    async function fetchPdfBlobUrl() {
        const r = await api.get(`/work-orders/${id}/pdf`, { responseType: "blob" });
        return window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    }
    async function downloadPdf() {
        try {
            const url = await fetchPdfBlobUrl();
            const a = document.createElement("a");
            a.href = url; a.download = `${wo.work_order_number}-Job-Sheet.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatError(e)); }
    }
    async function printPdf() {
        try {
            const url = await fetchPdfBlobUrl();
            const win = window.open(url, "_blank");
            if (win) win.onload = () => { try { win.print(); } catch (_) { } };
        } catch (e) { toast.error(formatError(e)); }
    }

    if (loading || !wo) return <LoadingState message="Loading work order..." />;

    return (
        <div className="space-y-6" data-testid="wo-detail-dialog">
            <div className="border-b border-brand-line pb-4">
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft font-mono" data-testid="wo-detail-number">{wo.work_order_number}</p>
                        <h2 className="font-heading text-xl md:text-2xl tracking-tight text-brand-ink mt-1" data-testid="wo-detail-title">{wo.title}</h2>
                        {wo.source_complaint_number && (
                            <p className="text-xs text-brand-inkSoft mt-1">
                                From <Link to="/app/complaints" className="text-brand-action hover:underline inline-flex items-center gap-1" data-testid="wo-detail-source-link">{wo.source_complaint_number}<ArrowSquareOut size={10} /></Link>
                            </p>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-2 items-start">
                        <Chip variant={PRIORITY_VARIANT[wo.priority] || "default"}>{label(wo.priority)}</Chip>
                        <Chip variant={STATUS_VARIANT[wo.status] || "default"} data-testid="wo-detail-status">{label(wo.status)}</Chip>
                        {wo.due_state && <Chip variant={DUE_VARIANT[wo.due_state]}>{dueLabel(wo)}</Chip>}
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <Button data-testid="wo-download-pdf-btn" onClick={downloadPdf} variant="outline" size="sm" className="rounded-full">
                        <DownloadSimple size={14} className="mr-1.5" weight="bold" /> Download PDF
                    </Button>
                    <Button data-testid="wo-print-pdf-btn" onClick={printPdf} variant="outline" size="sm" className="rounded-full">
                        <Printer size={14} className="mr-1.5" weight="duotone" /> Print
                    </Button>
                </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Type</span><span className="font-medium text-brand-ink">{label(wo.type)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Origin</span><span className="font-medium text-brand-ink">{label(wo.origin_type)}</span></div>
                    {wo.origin_type === "PREVENTIVE" && wo.preventive_plan_id && (
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Preventive Plan</span>
                            <Link to="/app/preventive-maintenance" state={{ openPlanId: wo.preventive_plan_id }}
                                className="text-brand-action hover:underline inline-flex items-center gap-1 font-medium" data-testid="wo-detail-preventive-plan-link">
                                {wo.preventive_plan_name || "View Plan"}<ArrowSquareOut size={10} />
                            </Link>
                        </div>
                    )}
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Target</span><span className="font-medium text-brand-ink">{wo.target_type === "COMMON_AREA" ? <><Buildings size={11} weight="duotone" className="inline mr-1" />{wo.common_area}</> : wo.target_type === "RESIDENT" ? <><HouseLine size={11} weight="duotone" className="inline mr-1" />{wo.target_flat_label} · {wo.target_resident_name || "—"}</> : "—"}</span></div>
                    {wo.common_area_location && <div className="flex justify-between"><span className="text-brand-inkSoft">Location</span><span className="font-medium text-brand-ink">{wo.common_area_location}</span></div>}
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Category</span><span className="font-medium text-brand-ink">{wo.category ? label(wo.category) : "—"}</span></div>
                </div>
                <div className="space-y-2">
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Created</span><span className="font-medium text-brand-ink">{fmt(wo.created_at)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Due</span><span className="font-medium text-brand-ink">{fmtDate(wo.due_date)}</span></div>
                    {wo.completed_at && <div className="flex justify-between"><span className="text-brand-inkSoft">Completed</span><span className="font-medium text-brand-ink">{fmt(wo.completed_at)}</span></div>}
                    {wo.closed_at && <div className="flex justify-between"><span className="text-brand-inkSoft">Closed</span><span className="font-medium text-brand-ink">{fmt(wo.closed_at)}</span></div>}
                    {wo.estimated_cost != null && <div className="flex justify-between"><span className="text-brand-inkSoft">Est. cost</span><span className="font-medium text-brand-ink">₹{wo.estimated_cost.toLocaleString("en-IN")}</span></div>}
                    {wo.actual_cost != null && <div className="flex justify-between"><span className="text-brand-inkSoft">Actual cost</span><span className="font-medium text-brand-ink">₹{wo.actual_cost.toLocaleString("en-IN")}</span></div>}
                </div>
            </div>

            {wo.description && (
                <div className="bg-brand-bg border border-brand-line rounded-sm p-4">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-1">Description</p>
                    <p className="text-sm text-brand-ink whitespace-pre-wrap">{wo.description}</p>
                </div>
            )}

            {wo.assigned_provider_snapshot && (
                <div className="bg-brand-sage/40 border border-brand-line rounded-sm p-4" data-testid="wo-detail-assignee">
                    <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-1">Assigned {wo.assigned_provider_snapshot.type === "VENDOR" ? "vendor" : "staff"}</p>
                    <p className="font-medium text-brand-ink">{wo.assigned_provider_snapshot.name}{wo.assigned_provider_snapshot.vendor_org ? ` · ${wo.assigned_provider_snapshot.vendor_org}` : ""}</p>
                    <p className="text-xs text-brand-inkSoft">{wo.assigned_provider_snapshot.role_label}</p>
                </div>
            )}

            {wo.completion_summary && (
                <div className="bg-[#ECFDF5]/40 border border-[#059669]/20 rounded-sm p-4" data-testid="wo-detail-completion">
                    <p className="text-[10px] uppercase tracking-overline text-[#059669] mb-1 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Completion</p>
                    <p className="text-sm text-brand-ink whitespace-pre-wrap">{wo.completion_summary}</p>
                </div>
            )}

            {/* Financial section */}
            <div className="bg-white border border-brand-line rounded-sm p-4 space-y-3" data-testid="wo-financial-section">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft flex items-center gap-1"><CurrencyInr size={12} weight="duotone" /> Financial</p>
                <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Responsibility</span>
                        <span className="font-medium text-brand-ink" data-testid="wo-financial-responsibility">{FR_LABEL[wo.financial_responsibility] || wo.financial_responsibility}</span>
                    </div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Financial Status</span>
                        <Chip variant={wo.financial_status === "POSTED" ? "success" : "neutral"} data-testid="wo-financial-status">{wo.financial_status === "POSTED" ? "Posted" : "Not Posted"}</Chip>
                    </div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Estimated cost</span><span className="font-medium text-brand-ink">{wo.estimated_cost != null ? `₹${wo.estimated_cost.toLocaleString("en-IN")}` : "—"}</span></div>
                    <div className="flex justify-between"><span className="text-brand-inkSoft">Actual cost</span><span className="font-medium text-brand-ink">{wo.actual_cost != null ? `₹${wo.actual_cost.toLocaleString("en-IN")}` : "—"}</span></div>
                </div>

                {wo.financial_status !== "POSTED" ? (
                    <div className="grid md:grid-cols-2 gap-3 pt-2 border-t border-brand-line/60">
                        <div>
                            <Label className="text-xs">Financial Responsibility</Label>
                            <Select value={wo.financial_responsibility} onValueChange={setResponsibility}>
                                <SelectTrigger data-testid="wo-fr-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="SOCIETY">Society</SelectItem>
                                    <SelectItem value="FLAT" disabled={wo.target_type !== "RESIDENT"}>Flat</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-[11px] text-brand-inkSoft mt-1">
                                {wo.financial_responsibility === "FLAT" ? `Cost will be invoiced to ${wo.target_flat_label || "this flat"}.` : "Cost will be recorded as Society Expense."}
                            </p>
                        </div>
                        <div>
                            <Label className="text-xs">Approved cost (₹)</Label>
                            <div className="flex gap-2">
                                <Input data-testid="wo-approved-cost-input" type="number" step="0.01" value={approvedCostInput} onChange={(e) => setApprovedCostInput(e.target.value)} className="rounded-sm border-brand-line" />
                                <Button type="button" onClick={saveApprovedCost} variant="outline" className="rounded-full shrink-0">Save</Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="pt-2 border-t border-brand-line/60 flex items-center justify-between flex-wrap gap-2" data-testid="wo-financial-posted-block">
                        <div className="text-sm">
                            <span className="text-brand-inkSoft">{wo.financial_transaction_type === "INVOICE" ? "Invoice" : "Expense"}: </span>
                            <span className="font-medium text-brand-ink">{wo.financial_transaction_number}</span>
                        </div>
                        {wo.financial_transaction_type === "INVOICE" ? (
                            <Link to={`/app/invoices/${wo.financial_transaction_id}`} className="text-brand-action hover:underline inline-flex items-center gap-1 text-sm" data-testid="wo-financial-view-link">
                                View Invoice<ArrowSquareOut size={12} />
                            </Link>
                        ) : (
                            <Link to="/app/expenses" className="text-brand-action hover:underline inline-flex items-center gap-1 text-sm" data-testid="wo-financial-view-link">
                                View Expenses<ArrowSquareOut size={12} />
                            </Link>
                        )}
                    </div>
                )}

                {["COMPLETED", "CLOSED"].includes(wo.status) && wo.financial_status !== "POSTED" && (
                    <>
                        <Button data-testid="wo-post-financial-btn" disabled={wo.approved_cost == null && wo.actual_cost == null}
                            onClick={() => setPostOpen(true)} className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white disabled:opacity-50">
                            <Receipt size={14} className="mr-1.5" weight="duotone" /> Post Financial Transaction
                        </Button>
                        {wo.approved_cost == null && wo.actual_cost == null && (
                            <p className="text-xs text-brand-inkSoft italic">Set an approved cost above before posting.</p>
                        )}
                    </>
                )}
            </div>

            {/* Staff actions */}
            <div className="bg-white border border-brand-line rounded-sm p-4 space-y-3" data-testid="wo-staff-actions">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Actions</p>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">Assign to</Label>
                        <Select value={wo.assigned_provider_id || ""} onValueChange={assign}>
                            <SelectTrigger data-testid="wo-assign-select" className="rounded-sm border-brand-line"><SelectValue placeholder="Pick provider" /></SelectTrigger>
                            <SelectContent>{providers.filter(p => p.is_active).map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.entity_type}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs">Status</Label>
                        <Select value={wo.status} onValueChange={(v) => v !== wo.status && changeStatus(v)}>
                            <SelectTrigger data-testid="wo-status-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {STATUSES.filter(s => s !== "COMPLETED" || wo.status === "COMPLETED").map(s => (
                                    <SelectItem key={s} value={s} disabled={s === "COMPLETED"}>{label(s)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                {wo.status === "IN_PROGRESS" && (
                    <Button data-testid="wo-complete-btn" onClick={() => setCompleteOpen(true)} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <CheckCircle size={16} className="mr-1.5" /> Mark Completed
                    </Button>
                )}
                {["ASSIGNED", "ON_HOLD"].includes(wo.status) && (
                    <p className="text-xs text-brand-inkSoft italic">Move status to "In Progress" to unlock Mark Completed.</p>
                )}
                {wo.status === "COMPLETED" && (
                    <Button data-testid="wo-close-btn" onClick={closeWO} className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white">Close Work Order</Button>
                )}
            </div>

            {/* Comments */}
            <div className="border-t border-brand-line pt-4">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3">Notes & Comments</p>
                <div className="space-y-2 mb-3" data-testid="wo-comments">
                    {comments.map(c => (
                        <div key={c.id} className={`rounded-sm p-2 text-sm ${c.kind === "internal" ? "bg-brand-action/10 border border-brand-action/30" : "bg-brand-bg border border-brand-line"}`}>
                            <div className="flex justify-between text-xs">
                                <span className="font-medium text-brand-ink">{c.user_name}{c.kind === "internal" && <span className="ml-1 text-brand-action inline-flex items-center gap-1"><LockKey size={10} weight="fill" />Internal</span>}</span>
                                <span className="text-brand-inkSoft">{fmt(c.at)}</span>
                            </div>
                            <p className="text-brand-ink mt-1 whitespace-pre-wrap">{c.body}</p>
                        </div>
                    ))}
                    {comments.length === 0 && <p className="text-xs text-brand-inkSoft italic">No notes yet.</p>}
                </div>
                <Textarea data-testid="wo-comment-input" rows={2} value={commentBody} onChange={(e) => setCommentBody(e.target.value)} placeholder="Add a note..." className="rounded-sm border-brand-line" />
                <div className="flex items-center justify-between mt-2">
                    <Select value={commentKind} onValueChange={setCommentKind}>
                        <SelectTrigger data-testid="wo-comment-kind" className="h-9 rounded-sm border-brand-line w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="public">Public note</SelectItem>
                            <SelectItem value="internal">Internal note</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button data-testid="wo-post-comment-btn" onClick={postComment} className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-white">Post</Button>
                </div>
            </div>

            {/* Timeline */}
            <div className="border-t border-brand-line pt-4">
                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft mb-3 flex items-center gap-1"><ClockClockwise size={12} weight="duotone" /> Timeline</p>
                <ul className="space-y-3" data-testid="wo-timeline">
                    {timeline.map(ev => (
                        <li key={ev.id} className="text-xs">
                            <p className="text-brand-inkSoft">{fmt(ev.at)} · <span className="font-medium text-brand-ink">{ev.user_name}</span>{ev.is_internal && <span className="ml-1 text-brand-action">· internal</span>}</p>
                            <p className="text-brand-ink font-medium mt-0.5">
                                {ev.action === "created" && "Work order created"}
                                {ev.action === "assigned" && `Assigned to ${ev.new_value?.name || "—"}`}
                                {ev.action === "status_changed" && `Status: ${label(ev.old_value)} → ${label(ev.new_value)}`}
                                {ev.action === "completed" && "Marked completed"}
                                {ev.action === "closed" && "Closed"}
                                {ev.action === "comment_added" && "Public note"}
                                {ev.action === "internal_note_added" && "Internal note"}
                                {ev.action === "attachment_added" && "Photo attached"}
                                {ev.action === "financial_posted" && `Financial transaction posted — ${ev.new_value?.transaction_type === "INVOICE" ? "Invoice" : "Expense"} ${ev.new_value?.transaction_number} (₹${Number(ev.new_value?.amount || 0).toLocaleString("en-IN")})`}
                                {["priority_updated", "due_date_updated", "type_updated", "financial_responsibility_updated", "approved_cost_updated"].includes(ev.action) && label(ev.action)}
                            </p>
                            {ev.comment && <p className="italic text-brand-inkSoft mt-0.5">"{ev.comment}"</p>}
                        </li>
                    ))}
                </ul>
            </div>

            {/* Complete modal */}
            <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Complete Work Order</DialogTitle>
                        <DialogDescription>Completion summary is required.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div><Label>Summary *</Label><Textarea data-testid="wo-completion-summary" rows={3} value={completion.summary} onChange={(e) => setCompletion({ ...completion, summary: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Water pump replaced and tested" /></div>
                        <div><Label>Actual cost (₹)</Label><Input data-testid="wo-completion-cost" type="number" step="0.01" value={completion.cost} onChange={(e) => setCompletion({ ...completion, cost: e.target.value })} className="rounded-sm border-brand-line" /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCompleteOpen(false)} className="rounded-full">Cancel</Button>
                        <Button data-testid="wo-completion-submit" onClick={submitComplete} className="rounded-full bg-brand-action hover:bg-brand-actionHover">Complete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Post Financial Transaction confirm modal */}
            <Dialog open={postOpen} onOpenChange={setPostOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">Post Financial Transaction</DialogTitle>
                        <DialogDescription>Review before confirming — this creates a real {wo.financial_responsibility === "FLAT" ? "invoice against the flat" : "society expense"} and cannot be undone from here.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 text-sm" data-testid="wo-post-financial-review">
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Work Order</span><span className="font-medium text-brand-ink">{wo.work_order_number}</span></div>
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Responsibility</span><span className="font-medium text-brand-ink">{FR_LABEL[wo.financial_responsibility] || wo.financial_responsibility}</span></div>
                        {wo.financial_responsibility === "FLAT" && <div className="flex justify-between"><span className="text-brand-inkSoft">Target Flat</span><span className="font-medium text-brand-ink">{wo.target_flat_label || "—"}</span></div>}
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Approved cost</span><span className="font-medium text-brand-ink">{wo.approved_cost != null ? `₹${wo.approved_cost.toLocaleString("en-IN")}` : "—"}</span></div>
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Actual cost</span><span className="font-medium text-brand-ink">{wo.actual_cost != null ? `₹${wo.actual_cost.toLocaleString("en-IN")}` : "—"}</span></div>
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Vendor/Staff</span><span className="font-medium text-brand-ink">{wo.assigned_provider_snapshot?.name || "—"}</span></div>
                        <div className="flex justify-between"><span className="text-brand-inkSoft">Current financial status</span><span className="font-medium text-brand-ink">Not Posted</span></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPostOpen(false)} className="rounded-full">Cancel</Button>
                        <Button data-testid="wo-post-financial-confirm" disabled={posting} onClick={postFinancial} className="rounded-full bg-brand-action hover:bg-brand-actionHover">{posting ? "Posting..." : "Confirm & Post"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ---------- Main page ----------
export default function WorkOrders() {
    const { user } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const isStaff = user?.role === "admin" || user?.role === "committee";
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);

    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [priorityFilter, setPriorityFilter] = useState("all");
    const [originFilter, setOriginFilter] = useState("all");
    const [dueFilter, setDueFilter] = useState("all");
    const [sortBy, setSortBy] = useState("newest");
    const [selectedId, setSelectedId] = useState(null);

    const emptyForm = () => ({
        title: "", description: "", type: "OTHER", origin_type: "DIRECT",
        source_complaint_id: "", target_type: "COMMON_AREA",
        target_block_id: "", target_flat_id: "",
        common_area: "", common_area_location: "",
        priority: "medium", assigned_provider_id: "", due_date: "", estimated_cost: "",
        financial_responsibility: "SOCIETY", approved_cost: "",
    });
    const [createOpen, setCreateOpen] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const [flats, setFlats] = useState([]);

    async function load(overrideQ) {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter === "open_all" ? "open_all" : statusFilter;
            if (priorityFilter !== "all") params.priority = priorityFilter;
            if (originFilter !== "all") params.origin_type = originFilter;
            if (dueFilter !== "all") params.due = dueFilter;
            const qVal = overrideQ !== undefined ? overrideQ : q;
            if (qVal.trim()) params.q = qVal.trim();
            if (sortBy) params.sort = sortBy;
            const [list, sm, prov] = await Promise.all([
                api.get("/work-orders", { params }),
                api.get("/work-orders/summary"),
                api.get("/staff", { params: { active: "true" } }),
            ]);
            setRows(list.data); setSummary(sm.data); setProviders(prov.data);
        } catch (e) { toast.error(formatError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { if (isStaff) load(); /* eslint-disable-next-line */ }, [statusFilter, priorityFilter, originFilter, dueFilter, sortBy]);

    useEffect(() => { if (isStaff) api.get("/flats").then(r => setFlats(r.data || [])).catch(() => { }); }, [isStaff]);

    // Dashboard Quick Action deep-link: navigate here with state={{ autoOpenCreate: true }}
    useEffect(() => {
        if (isStaff && location.state?.autoOpenCreate) {
            setForm(emptyForm());
            setCreateOpen(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStaff]);

    // Flat 360 Profile deep-link: navigate here with state={{ prefillSearch: "C-202" }}
    useEffect(() => {
        if (isStaff && location.state?.prefillSearch) {
            setQ(location.state.prefillSearch);
            load(location.state.prefillSearch);
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStaff]);

    const blocks = useMemo(() => Array.from(new Set(flats.map(f => f.block).filter(Boolean))).sort(), [flats]);
    const flatsForBlock = useMemo(() => flats.filter(f => f.block === form.target_block_id), [flats, form.target_block_id]);

    async function submitCreate(e) {
        e.preventDefault();
        if (!form.title.trim()) { toast.error("Title is required"); return; }
        if (form.target_type === "RESIDENT" && !form.target_flat_id) { toast.error("Select a flat"); return; }
        if (form.target_type === "COMMON_AREA" && !form.common_area) { toast.error("Select a common area"); return; }
        const payload = { ...form };
        if (payload.estimated_cost) payload.estimated_cost = parseFloat(payload.estimated_cost); else delete payload.estimated_cost;
        if (payload.approved_cost) payload.approved_cost = parseFloat(payload.approved_cost); else delete payload.approved_cost;
        for (const k of ["source_complaint_id", "assigned_provider_id", "due_date", "target_flat_id", "common_area", "common_area_location", "target_block_id"]) {
            if (!payload[k]) delete payload[k];
        }
        delete payload.target_block_id;
        try {
            await api.post("/work-orders", payload);
            toast.success("Work order created");
            setCreateOpen(false); setForm(emptyForm()); load();
        } catch (e) { toast.error(formatError(e)); }
    }

    if (!isStaff) return <EmptyState title="Not available" description="Only admin/committee can access Work Orders." />;

    return (
        <div className="animate-fade-up space-y-6" data-testid="work-orders-page">
            <PageHeader
                overline="Operations"
                title="Work Orders"
                description="Operational execution — direct or complaint-linked jobs assigned to staff/vendors."
                actions={
                    <Button data-testid="wo-create-btn" onClick={() => { setForm(emptyForm()); setCreateOpen(true); }} className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <Plus size={16} className="mr-1.5" /> Create Work Order
                    </Button>
                }
            />

            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="wo-summary">
                    <StatCard label="Total" value={summary.total} testId="wo-summary-total" />
                    <StatCard label="Open" value={summary.total_open} accent testId="wo-summary-open" />
                    <StatCard label="In Progress" value={summary.by_status.IN_PROGRESS} testId="wo-summary-in-progress" />
                    <StatCard label="On Hold" value={summary.by_status.ON_HOLD} testId="wo-summary-on-hold" />
                    <StatCard label="Completed" value={summary.by_status.COMPLETED} testId="wo-summary-completed" />
                    <StatCard label="Overdue" value={summary.overdue} accent testId="wo-summary-overdue" />
                </div>
            )}

            {/* Filters */}
            <div className="bg-white border border-brand-line rounded-sm p-4" data-testid="wo-filters">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="md:col-span-2 relative">
                        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkSoft" />
                        <Input data-testid="wo-search" placeholder="Search WO#, title, complaint, provider..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="rounded-sm border-brand-line pl-9" />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="wo-status-filter" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All statuses</SelectItem>
                            <SelectItem value="open_all">Open (all)</SelectItem>
                            {STATUSES.map(s => <SelectItem key={s} value={s}>{label(s)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={originFilter} onValueChange={setOriginFilter}>
                        <SelectTrigger data-testid="wo-origin-filter" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All origins</SelectItem>
                            <SelectItem value="COMPLAINT">Complaint</SelectItem>
                            <SelectItem value="DIRECT">Direct</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={dueFilter} onValueChange={setDueFilter}>
                        <SelectTrigger data-testid="wo-due-filter" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All due dates</SelectItem>
                            <SelectItem value="overdue">Overdue</SelectItem>
                            <SelectItem value="today">Due today</SelectItem>
                            <SelectItem value="soon">Due soon</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="mt-3 flex justify-between flex-wrap gap-2">
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger data-testid="wo-sort" className="rounded-sm border-brand-line w-52"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="newest">Newest first</SelectItem>
                            <SelectItem value="oldest">Oldest first</SelectItem>
                            <SelectItem value="due">By due date</SelectItem>
                            <SelectItem value="priority">By priority</SelectItem>
                            <SelectItem value="status">By status</SelectItem>
                        </SelectContent>
                    </Select>
                    <div>
                        <Button variant="ghost" size="sm" onClick={load} className="text-brand-ink">Search</Button>
                        <Button variant="ghost" size="sm" onClick={() => { setQ(""); setStatusFilter("all"); setPriorityFilter("all"); setOriginFilter("all"); setDueFilter("all"); setSortBy("newest"); }} className="text-brand-inkSoft">Clear</Button>
                    </div>
                </div>
            </div>

            {loading ? <LoadingState /> : rows.length === 0 ? <EmptyState title="No work orders yet" description="Create a direct work order or generate one from a complaint." /> : (
                <div className="bg-white border border-brand-line rounded-sm overflow-hidden" data-testid="wo-list">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[1000px]">
                            <thead>
                                <tr className="bg-brand-bg border-b border-brand-line text-left">
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">WO #</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Title</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Type</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Origin</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Target</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Assigned</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Priority</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Status</th>
                                    <th className="py-2 px-4 text-[10px] uppercase tracking-overline text-brand-inkSoft">Due</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.id} data-testid={`wo-row-${r.id}`} onClick={() => setSelectedId(r.id)} className="border-b border-brand-line/60 hover:bg-brand-bg/50 cursor-pointer">
                                        <td className="py-3 px-4 font-mono text-xs text-brand-ink">{r.work_order_number}</td>
                                        <td className="py-3 px-4 text-brand-ink font-medium">{r.title}
                                            {r.source_complaint_number && <p className="text-[10px] text-brand-inkSoft mt-0.5">from {r.source_complaint_number}</p>}
                                        </td>
                                        <td className="py-3 px-4 text-brand-inkSoft">{label(r.type)}</td>
                                        <td className="py-3 px-4"><Chip variant={r.origin_type === "COMPLAINT" ? "default" : "neutral"}>{label(r.origin_type)}</Chip></td>
                                        <td className="py-3 px-4 text-xs">
                                            {r.target_type === "COMMON_AREA" ? <><Buildings size={11} weight="duotone" className="inline mr-1" />{r.common_area}</> : r.target_type === "RESIDENT" ? <><HouseLine size={11} weight="duotone" className="inline mr-1" />{r.target_flat_label} · {r.target_resident_name || "—"}</> : "—"}
                                        </td>
                                        <td className="py-3 px-4 text-xs text-brand-inkSoft">{r.assigned_provider_name || "—"}</td>
                                        <td className="py-3 px-4"><Chip variant={PRIORITY_VARIANT[r.priority] || "default"}>{label(r.priority)}</Chip></td>
                                        <td className="py-3 px-4"><Chip variant={STATUS_VARIANT[r.status] || "default"}>{label(r.status)}</Chip></td>
                                        <td className="py-3 px-4">{r.due_state ? <Chip variant={DUE_VARIANT[r.due_state]}>{dueLabel(r)}</Chip> : <span className="text-xs text-brand-inkSoft">—</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create dialog */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="rounded-sm max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">Create Work Order</DialogTitle>
                        <DialogDescription>Direct work order — for complaint-linked, use the "Create Work Order" button inside a complaint.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submitCreate} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div><Label>Title *</Label><Input data-testid="wo-form-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm border-brand-line" /></div>
                            <div><Label>Type</Label>
                                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                                    <SelectTrigger data-testid="wo-form-type" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{TYPES.filter(t => t !== "COMPLAINT").map(t => <SelectItem key={t} value={t}>{label(t)}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div><Label>Description</Label><Textarea data-testid="wo-form-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm border-brand-line" /></div>
                        <div className="space-y-2 border border-brand-line rounded-sm p-3 bg-brand-bg">
                            <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Target *</Label>
                            <div className="flex gap-2">
                                <Button type="button" onClick={() => setForm({ ...form, target_type: "COMMON_AREA", financial_responsibility: "SOCIETY" })} data-testid="wo-target-common"
                                    className={`flex-1 rounded-full ${form.target_type === "COMMON_AREA" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}><Buildings size={14} className="mr-1" />Common Area</Button>
                                <Button type="button" onClick={() => setForm({ ...form, target_type: "RESIDENT", financial_responsibility: "FLAT" })} data-testid="wo-target-resident"
                                    className={`flex-1 rounded-full ${form.target_type === "RESIDENT" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}><HouseLine size={14} className="mr-1" />Resident</Button>
                            </div>
                            {form.target_type === "COMMON_AREA" ? (
                                <div className="grid md:grid-cols-2 gap-2 pt-2">
                                    <div><Label className="text-xs">Common Area *</Label>
                                        <Select value={form.common_area} onValueChange={(v) => setForm({ ...form, common_area: v })}>
                                            <SelectTrigger data-testid="wo-form-common-area" className="rounded-sm border-brand-line"><SelectValue placeholder="Pick area" /></SelectTrigger>
                                            <SelectContent>{COMMON_AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="text-xs">Location</Label><Input data-testid="wo-form-common-loc" value={form.common_area_location} onChange={(e) => setForm({ ...form, common_area_location: e.target.value })} className="rounded-sm border-brand-line" placeholder="e.g. Tower A, 3rd Floor" /></div>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 gap-2 pt-2">
                                    <div><Label className="text-xs">Block *</Label>
                                        <Select value={form.target_block_id} onValueChange={(v) => setForm({ ...form, target_block_id: v, target_flat_id: "" })}>
                                            <SelectTrigger data-testid="wo-form-block" className="rounded-sm border-brand-line"><SelectValue placeholder="Block" /></SelectTrigger>
                                            <SelectContent>{blocks.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div><Label className="text-xs">Flat *</Label>
                                        <Select value={form.target_flat_id} onValueChange={(v) => setForm({ ...form, target_flat_id: v })}>
                                            <SelectTrigger data-testid="wo-form-flat" className="rounded-sm border-brand-line"><SelectValue placeholder={form.target_block_id ? "Pick flat" : "Pick block first"} /></SelectTrigger>
                                            <SelectContent>{flatsForBlock.map(f => <SelectItem key={f.id} value={f.id}>{f.block}-{f.number || f.label}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="space-y-2 border border-brand-line rounded-sm p-3 bg-brand-bg">
                            <Label className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Financial Responsibility</Label>
                            <div className="flex gap-2">
                                <Button type="button" onClick={() => setForm({ ...form, financial_responsibility: "SOCIETY" })} data-testid="wo-fr-society-btn"
                                    className={`flex-1 rounded-full ${form.financial_responsibility === "SOCIETY" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}><Buildings size={14} className="mr-1" />Society</Button>
                                <Button type="button" disabled={form.target_type !== "RESIDENT"} onClick={() => setForm({ ...form, financial_responsibility: "FLAT" })} data-testid="wo-fr-flat-btn"
                                    className={`flex-1 rounded-full disabled:opacity-40 disabled:cursor-not-allowed ${form.financial_responsibility === "FLAT" ? "bg-brand-ink text-white" : "bg-white border border-brand-line text-brand-ink"}`}><HouseLine size={14} className="mr-1" />Flat</Button>
                            </div>
                            <p className="text-xs text-brand-inkSoft" data-testid="wo-fr-helper">
                                {form.financial_responsibility === "FLAT" ? "Cost will be invoiced to this flat." : "Cost will be recorded as Society Expense."}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div><Label>Priority</Label>
                                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                                    <SelectTrigger data-testid="wo-form-priority" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{label(p)}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Assign to</Label>
                                <Select value={form.assigned_provider_id} onValueChange={(v) => setForm({ ...form, assigned_provider_id: v })}>
                                    <SelectTrigger data-testid="wo-form-assignee" className="rounded-sm border-brand-line"><SelectValue placeholder="Optional" /></SelectTrigger>
                                    <SelectContent>{providers.map(p => <SelectItem key={p.id} value={p.id}>{p.name} · {p.entity_type}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div><Label>Due date</Label><Input data-testid="wo-form-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm border-brand-line" /></div>
                            <div><Label>Estimated cost (₹)</Label><Input data-testid="wo-form-est-cost" type="number" step="0.01" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} className="rounded-sm border-brand-line" /></div>
                            <div><Label>Approved cost (₹)</Label><Input data-testid="wo-form-approved-cost" type="number" step="0.01" value={form.approved_cost} onChange={(e) => setForm({ ...form, approved_cost: e.target.value })} className="rounded-sm border-brand-line" /></div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="wo-form-submit" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Create</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={!!selectedId} onOpenChange={(v) => !v && setSelectedId(null)}>
                <DialogContent className="rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader className="sr-only">
                        <DialogTitle>Work Order details</DialogTitle>
                        <DialogDescription>Full work order lifecycle, timeline and comments.</DialogDescription>
                    </DialogHeader>
                    <button onClick={() => setSelectedId(null)} className="absolute right-4 top-4 text-brand-inkSoft hover:text-brand-ink"><X size={18} /></button>
                    {selectedId && <WODetail id={selectedId} providers={providers} onClose={() => setSelectedId(null)} onChange={load} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
